import type {MaterialsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import type {GeoObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {EventsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/EventsNetwork';
import type {PerspectiveCameraObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/PerspectiveCamera';
import type {RootManagerNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		function create_MAT(geo1: GeoObjNode) {
			var MAT = geo1.createNode('materialsNetwork');
			MAT.setName('MAT');
			function create_meshNormal1(MAT: MaterialsNetworkSopNode) {
				var meshNormal1 = MAT.createNode('meshNormal');
				meshNormal1.setName('meshNormal1');
				meshNormal1.uiData.setPosition(0, 100);
				meshNormal1.params.postCreateSpareParams();
				meshNormal1.params.runOnSceneLoadHooks();
				return meshNormal1;
			}
			create_meshNormal1(MAT);
			MAT.uiData.setPosition(-350, 250);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return MAT;
		}
		function create_fileGLTF1(geo1: GeoObjNode) {
			var fileGLTF1 = geo1.createNode('fileGLTF');
			fileGLTF1.setName('fileGLTF1');
			fileGLTF1.uiData.setPosition(-150, 50);
			fileGLTF1.p.url.set(
				'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/models/resources/threedscans.com/theodoric_the_great.glb'
			);
			fileGLTF1.params.postCreateSpareParams();
			fileGLTF1.params.runOnSceneLoadHooks();
			return fileGLTF1;
		}
		function create_material1(geo1: GeoObjNode) {
			var material1 = geo1.createNode('material');
			material1.setName('material1');
			material1.uiData.setPosition(-150, 250);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshNormal1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return material1;
		}
		create_MAT(geo1);
		var fileGLTF1 = create_fileGLTF1(geo1);
		var material1 = create_material1(geo1);
		material1.setInput(0, fileGLTF1);
		geo1.uiData.setPosition(-50, -150);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return geo1;
	}
	function create_grid(parentNode: RootManagerNode) {
		var grid = parentNode.createNode('geo');
		grid.setName('grid');
		function create_planeHelper1(grid: GeoObjNode) {
			var planeHelper1 = grid.createNode('planeHelper');
			planeHelper1.setName('planeHelper1');
			planeHelper1.uiData.setPosition(0, 0);
			planeHelper1.flags.display.set(true);
			planeHelper1.params.postCreateSpareParams();
			planeHelper1.params.runOnSceneLoadHooks();
			return planeHelper1;
		}
		create_planeHelper1(grid);
		grid.uiData.setPosition(-50, -250);
		grid.flags.display.set(true);
		grid.params.postCreateSpareParams();
		grid.params.runOnSceneLoadHooks();
		return grid;
	}
	function create_perspectiveCamera1(parentNode: RootManagerNode) {
		var perspectiveCamera1 = parentNode.createNode('perspectiveCamera');
		perspectiveCamera1.setName('perspectiveCamera1');
		function create_events1(perspectiveCamera1: PerspectiveCameraObjNode) {
			var events1 = perspectiveCamera1.createNode('eventsNetwork');
			events1.setName('events1');
			function create_cameraOrbitControls1(events1: EventsNetworkSopNode) {
				var cameraOrbitControls1 = events1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				cameraOrbitControls1.uiData.setPosition(150, 50);
				cameraOrbitControls1.p.target.set([-0.04003207776900154, 0.45369634017218685, -0.07522770510602053]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return cameraOrbitControls1;
			}
			create_cameraOrbitControls1(events1);
			events1.uiData.setPosition(-200, 50);
			events1.params.postCreateSpareParams();
			events1.params.runOnSceneLoadHooks();
			return events1;
		}
		create_events1(perspectiveCamera1);
		perspectiveCamera1.uiData.setPosition(-200, 100);
		perspectiveCamera1.flags.display.set(true);
		perspectiveCamera1.p.t.set([0.11254789339849623, 0.7354562476316117, 1.2755973471727238]);
		perspectiveCamera1.p.r.set([-11.782029169623556, 6.3097578558789005, 1.3132231598444772]);
		perspectiveCamera1.p.s.set([0.9999999999999999, 0.9999999999999998, 1]);
		perspectiveCamera1.p.controls.set('./events1/cameraOrbitControls1');
		perspectiveCamera1.params.postCreateSpareParams();
		perspectiveCamera1.params.runOnSceneLoadHooks();
		return perspectiveCamera1;
	}
	function create_hemisphereLight1(parentNode: RootManagerNode) {
		var hemisphereLight1 = parentNode.createNode('hemisphereLight');
		hemisphereLight1.setName('hemisphereLight1');
		hemisphereLight1.uiData.setPosition(150, 100);
		hemisphereLight1.flags.display.set(true);
		hemisphereLight1.params.postCreateSpareParams();
		hemisphereLight1.params.runOnSceneLoadHooks();
		return hemisphereLight1;
	}
	var geo1 = create_geo1(root);
	create_grid(root);
	var perspectiveCamera1 = create_perspectiveCamera1(root);
	create_hemisphereLight1(root);
	return {
		scene,
		cameraPath: perspectiveCamera1.path(),
		focusedNodes: [geo1.nodesByType('material')[0]],
		autoLayout: false,
	};
}
// export const matMeshNormal = [
// 	new Example({
// 		name: `${ExamplePrefix.BY_NODE}/mat/meshNormal`,
// 		createData,
// 		relatedNodes: [MeshNormalMatNode],
// 	}),
// ];
